import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import NotFound from '../../components/UI/NotFound';

const GuideVideo = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [formData, setFormData] = useState({
        type: ''
    });

    const fetchData = async () => {
        try {
            setLoader(true);
            const resp = await Calling.getGuide();

            if (resp.status) {
                setData(resp.data || []);
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleVideoChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validate file type (mp4 only)
        if (file.type !== 'video/mp4') {
            toast.error('Only MP4 video files are allowed');
            return;
        }

        // Validate file size (100MB limit)
        if (file.size > 100 * 1024 * 1024) {
            toast.error('Video size must be less than 100MB');
            return;
        }

        setVideoFile(file);

        // Create video preview URL
        const previewUrl = URL.createObjectURL(file);
        setVideoPreview(previewUrl);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.type.trim()) {
            toast.error('Type is required');
            return;
        }

        if (!currentVideo) {
            toast.error('No video selected for update');
            return;
        }

        const videoData = new FormData();
        videoData.append('type', formData.type);

        if (videoFile) {
            videoData.append('video', videoFile);
        }

        try {
            setLoader(true);
            const resp = await Calling.updateGuide(currentVideo._id, videoData);

            if (resp.status) {
                toast.success('Video updated successfully');
                resetForm();
            } else {
                toast.error(resp.message || 'Failed to update video');
            }
        } catch (error) {
            console.error(error);
            toast.error('An error occurred');
        } finally {
            setLoader(false);
            fetchData();

        }
    };

    const handleEdit = (video) => {
        setCurrentVideo(video);
        setFormData({
            type: video.type || ''
        });
        setVideoPreview(video.url);
        setEditMode(true);
    };

    const resetForm = () => {
        setFormData({ type: '' });
        setVideoFile(null);
        setVideoPreview(null);
        setCurrentVideo(null);
        setEditMode(false);
    };

    if (loader) return (<Loader loader={loader} />);

    return (
        <div className="px-3 sm:px-4 lg:ml-[4.4rem] w-full text-main-font bg-main-bg mt-16 md:mt-20">
            <div className="w-full mx-auto">
                {/* Form Section - Only visible in edit mode */}
                {editMode && (
                    <div className="bg-main-white p-5 rounded-lg shadow-md mb-8 border-l-4 border-main-yellow">
                        <h2 className="text-xl font-semibold mb-4 text-main-blue">Update Video</h2>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-main-font">
                                    Type
                                </label>
                                <input
                                    type="text"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-main-border rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue bg-main-bg"
                                    placeholder="Enter video type"
                                    disabled={true}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-main-font">
                                    Video File
                                    <span className="text-xs text-main-subfont ml-1">
                                        (MP4 only, max 100MB)
                                    </span>
                                </label>
                                <div className="border-2 border-dashed border-main-blue rounded-lg p-4 bg-main-th hover:bg-main-thHover transition-colors">
                                    <input
                                        type="file"
                                        accept="video/mp4"
                                        onChange={handleVideoChange}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            {videoPreview && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium mb-2 text-main-font">Preview</label>
                                    <div className="relative overflow-hidden rounded-lg shadow-lg border-2 border-main-blue bg-black">
                                        <video
                                            src={videoPreview}
                                            controls
                                            controlsList="nodownload"
                                            playsInline
                                            className="w-full max-h-80 mx-auto"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-4 pt-2">
                                <button
                                    type="submit"
                                    className="px-6 py-3 bg-main-blue text-white rounded-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-opacity-50 transition-colors shadow-md"
                                >
                                    Update Video
                                </button>

                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 bg-main-border text-main-font rounded-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-main-border focus:ring-opacity-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Video List Section */}
                <div className="bg-main-white p-5 rounded-lg shadow-md">
                    <h1 className="text-2xl font-bold mb-2 text-main-blue">Guide Videos</h1>

                    {data.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-10 bg-main-th rounded-lg">
                            <NotFound />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {data.map((video) => (
                                <div key={video._id} className="bg-white rounded-xl overflow-hidden shadow-md border border-main-border hover:shadow-lg transition-shadow">
                                    <div className="relative">
                                        <video
                                            src={video.url}
                                            className="w-full h-52 object-cover bg-black"
                                            controls
                                            controlsList="nodownload"
                                            playsInline
                                            preload="metadata"
                                        />
                                        <div className="absolute top-2 right-2 bg-main-blue text-white px-2 py-1 rounded-full text-xs">
                                            {video.type}
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-main-font">{video.type}</span>
                                            <button
                                                onClick={() => handleEdit(video)}
                                                className="px-4 py-2 bg-main-th text-main-blue rounded-lg hover:bg-main-blue hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-main-blue"
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GuideVideo;